import { EcoaContent } from "@components/common/EconContent";
import { HbButton } from "@components/legacy/HbButton";
import { EcoaAuthenticateCodeFlax } from "@maven-rest/cdms/ecoa/EcoaAuthenticateCode.flax";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { notify, useRequest } from "@coralblack/flax";
import { Footer } from "@layouts/Footer";
import { useLangContext } from "@layouts/home/LangContext";
import { TwoFactorAuthType } from "maven-lib/dist/dto/entity";
import { sessionSlice } from "maven-lib/dist/states";
import { ENCRYPTED_ACCESS_CODE_KEY } from "./models/identityVerification.const";
import { IDENTITY_VERIFICATION_LANG } from "./models/identityVerification.msg";
import { HbCodeInput } from "@maven-surface/components/base/HbCodeInput";
import { HbDivider } from "@maven-surface/components/base/HbDivider";
import { HbParagraph } from "@maven-surface/components/base/HbParagraph";
import { CDMS_STUDY_STATUS_CODE } from "@maven-msg/code/cdms/studyStatus";
import { ECOA_ACCESS_CODE_LANG } from "@maven-msg/lang/ecoaAccessCode";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

const BIRTH_YEAR_LENGTH = 4;

interface IdentityVerificationByBirthProps {}

export function IdentityVerificationByBirth(props: IdentityVerificationByBirthProps) {
  useLangContext();

  const encryptedAccessCode = sessionStorage.getItem(ENCRYPTED_ACCESS_CODE_KEY) ?? "";

  const [birthYear, setBirthYear] = useState<string>("");

  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const dispatch = useDispatch();

  const { request: requestVerifyEcoaAccessToken } = useRequest(
    {
      ...EcoaAuthenticateCodeFlax.verifyEcoaAuthenticateCode({
        encryptedAccessCode,
        twoFactorAuthType: TwoFactorAuthType.Birth,
        twoFactorAuthValue: birthYear,
      }),
    },
    {
      success: (resp) => {
        sessionStorage.removeItem(ENCRYPTED_ACCESS_CODE_KEY);
        dispatch(sessionSlice.actions.signIn({ ...resp, redirectTo: "/pat/visits" }));
      },
      error: (error) => handleError(error),
    }
  );

  function handleBirthYearChange(code: string): void {
    setBirthYear(code);
  }

  function handleBirthYearFilledUp(): void {
    const inputs = nextButtonRef?.current?.parentElement?.getElementsByTagName("button");
    if (!inputs?.length) return;
    inputs[0]?.focus();
  }

  function handleNextButtonClick(): void {
    if (birthYear.trim().length !== BIRTH_YEAR_LENGTH) {
      return notify(MvnMessage(IDENTITY_VERIFICATION_LANG.IDENTITY_VERIFICATION_BIRTH_YEAR_REQUIRED), "WARN");
    }

    requestVerifyEcoaAccessToken();
  }

  function handleError(error: any): string {
    if (error?.code === CDMS_STUDY_STATUS_CODE.STUDY_CLOSED.code) {
      dispatch(sessionSlice.actions.signOut());
    }

    if (error?.code === "ECOA_AUTH_LOCKED") {
      return MvnMessage(IDENTITY_VERIFICATION_LANG.IDENTITY_VERIFICATION_MULTIPLE_FAILED);
    }

    return MvnMessage(IDENTITY_VERIFICATION_LANG.IDENTITY_VERIFICATION_ERROR_MESSAGE);
  }

  return (
    <>
      <EcoaContent className="app-ecoa-page-home-identity-verification" verticalAlign="CENTER">
        <HbParagraph align="CENTER" margin={2} color="GRAY" text={MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_BIRTH_TEXT_LABEL)} />
        <HbDivider space={1} />
        <HbParagraph bold={true} align="CENTER">
          {MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_BIRTH_PLACEHOLDER)}
        </HbParagraph>
        <HbCodeInput
          length={BIRTH_YEAR_LENGTH}
          code={birthYear}
          onValueChange={handleBirthYearChange}
          onInputCompleted={handleBirthYearFilledUp}
        />

        <HbDivider space={1} />
        <HbButton
          text={MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_INPUT_RESET_TEXT)}
          color="PRIMARY"
          disabled={!birthYear.trim().length}
          onClick={() => {
            setBirthYear("");
          }}
        ></HbButton>
      </EcoaContent>
      <Footer>
        <HbButton
          elementRef={nextButtonRef}
          iconRight="ChromeBackMirrored"
          text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_NEXT)}
          onClick={handleNextButtonClick}
        />
      </Footer>
    </>
  );
}
